// API Client for Fired Up Games HTTP Server
// This client handles all communication with the authentication and user management APIs

interface ApiError {
  code: number;
  message: string;
  details?: string;
}

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  country: string;
  emailVerified: boolean;
  avatarUrl?: string;
  createdAt: string;
  accountStatus: string;
  roles: string[];
}

interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface RecentScore {
  scoreId?: number; // Optional score ID for claiming specific scores
  playerName: string;
  score: number;
  location: string;
  date: string;
  data: {
    gameType?: string;
    achievedAt?: string;
    [key: string]: any;
  };
}

interface UserProfileData {
  playerId: number;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  email: string;
  displayName?: string;
  country: string;
  lastUpdated: string;
  createdAt: string;
}

interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  country?: string;
  avatarUrl?: string;
}

interface PlayerTopScore {
  gameId: number;
  gameName: string;
  topScore: number;
  location: string;
  achievedAt: string;
  metadata: {
    gameType?: string;
    difficulty?: string;
    character?: string;
    level?: number;
    lives?: number;
    rounds?: number;
    [key: string]: any;
  };
}

interface UserGameScore {
  scoreId: number;
  gameId: number;
  gameName: string;
  score: number;
  location: string;
  achievedAt: string;
  metadata: {
    gameType?: string;
    difficulty?: string;
    character?: string;
    level?: number;
    lives?: number;
    rounds?: number;
    [key: string]: any;
  };
  matchId: number;
  playerName?: string;
}

interface DashboardData {
  user: User;
  topScores: RecentScore[];
  topScoresByGame: PlayerTopScore[];
  statistics: {
    totalGamesPlayed: number;
    totalScore: number;
    averageScore: number;
    bestScore: number;
    gamesThisWeek: number;
    gamesThisMonth: number;
    favoriteLocation: string;
    favoriteGameMode: string;
  };
}

interface AdminUserSummary {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  country: string;
  emailVerified: boolean;
  accountStatus: string;
  roles: string[];
  createdAt: string;
  lastUpdated: string;
  lastActiveAt?: string;
  totalMatches: number;
  totalScores: number;
}

interface AdminUsersPage {
  users: AdminUserSummary[];
  page: number;
  pageSize: number;
  total: number;
}

interface MetricPoint {
  label: string;
  value: number;
}

interface AdminProductMetrics {
  startDate: string;
  endDate: string;
  totalUsers: number;
  newUsers: number;
  verifiedUsers: number;
  activeUsers: number;
  totalMatches: number;
  totalScores: number;
  claimedScores: number;
  anonymousScores: number;
  matchesByDay: MetricPoint[];
  registrationsByDay: MetricPoint[];
  matchesByGame: MetricPoint[];
  matchesByLocation: MetricPoint[];
  matchesByPlatform: MetricPoint[];
}

interface AdminAuditEntry {
  id: number;
  actorUserId: number;
  actorEmail: string;
  targetUserId?: number;
  targetEmail?: string;
  action: string;
  details: Record<string, unknown>;
  createdAt: string;
}

interface AdminAuditPage {
  entries: AdminAuditEntry[];
  page: number;
  pageSize: number;
  total: number;
}

// Match claim request
export interface ClaimMatchRequest {
  scoreId: number;
}

// Match claim data structure
interface MatchClaimData {
  matchId: string;
  title: {
    titleId: string;
    name: string;
    version: string;
  };
  match: {
    uniqueMatchId: string;
    durationSeconds: number;
    platform: string;
    data: Record<string, any>;
    location?: {
      name: string;
      locationId?: string;
      companyName?: string;
    };
    utcTimestamp?: string;
  };
  scores: RecentScore[];
  claimed: boolean;
}

// Match details for a specific match
interface MatchDetails {
  matchId: number;
  gameId: number;
  gameName: string;
  playedAt: string;
  platform: string;
  location: string;
  playerStats: {
    score: number;
    scoreId: number;
    shots?: number;
    hits?: number;
    accuracy?: number;
    kills?: number;
    assists?: number;
    headshots?: number;
    heartshots?: number;
    bonusKills?: number;
    badKills?: number;
    [key: string]: any;
  };
  matchData: {
    durationSeconds?: number;
    [key: string]: any;
  };
}

interface UserLocation {
  locationGuid: string;
  name: string;
  address: string;
  description?: string;
  websiteUrl: string;
  userId: number;
  registeredAt: string; // ISO string representation of Temporal.Instant
}

interface LocationRegistrationRequest {
  companyName: string;
  locationIdentifier?: string;
  address: string;
  description?: string;
  websiteUrl: string;
}

interface LocationRegistrationResponse {
  message: string;
  location: UserLocation;
}

interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName: string;
  country: string;
  avatarUrl?: string;
  turnstileToken: string;
}

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

interface ValidateTokenResponse {
  valid: boolean;
  user?: User;
}

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false; // Prevent concurrent refresh attempts
  private refreshAttempts: number = 0; // Track refresh attempts
  private readonly maxRefreshAttempts: number = 3; // Maximum refresh attempts before clearing tokens

  constructor(baseUrl: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000') {
    this.baseUrl = baseUrl;
    this.loadTokensFromStorage();
  }

  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('firedup_access_token');
    this.refreshToken = localStorage.getItem('firedup_refresh_token');
  }

  private storeTokensInStorage(): void {
    if (this.accessToken && this.refreshToken) {
      localStorage.setItem('firedup_access_token', this.accessToken);
      localStorage.setItem('firedup_refresh_token', this.refreshToken);
    }
  }

  private saveTokensToStorage(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.refreshAttempts = 0; // Reset refresh attempts on successful token save
    this.storeTokensInStorage();
  }

  private clearTokensFromStorage(): void {
    console.log('🧹 Clearing all authentication tokens and state');
    this.accessToken = null;
    this.refreshToken = null;
    this.isRefreshing = false; // Reset refresh state to prevent any ongoing refresh attempts
    this.refreshAttempts = 0; // Reset refresh attempts when clearing tokens
    localStorage.removeItem('firedup_access_token');
    localStorage.removeItem('firedup_refresh_token');
    localStorage.removeItem('firedup_user');
    console.log('✅ All tokens and auth state cleared');
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    console.log('🌐 Making request to:', url);
    console.log('🔧 Request options:', options);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
      console.log('🔐 Authorization header added');
    }

    console.log('📋 Request headers:', headers);

    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log('📡 Response status:', response.status);
    console.log('📡 Response ok:', response.ok);

    if (!response.ok) {
      let errorData: ApiError;
      try {
        const responseText = await response.text();
        console.log('❌ Error response text:', responseText);
        
        if (responseText.trim()) {
          errorData = JSON.parse(responseText);
        } else {
          errorData = {
            code: response.status,
            message: response.statusText || 'An error occurred',
          };
        }
      } catch (parseError) {
        console.error('❌ Error parsing error response:', parseError);
        errorData = {
          code: response.status,
          message: response.statusText || 'An error occurred',
        };
      }

      console.error('❌ Request failed:', errorData);

      // Don't attempt refresh for logout endpoint - just clear tokens and fail
      if (endpoint === '/auth/logout' && response.status === 401) {
        console.log('🚪 Logout failed with 401 - user likely deleted. Clearing tokens without retry.');
        this.clearTokensFromStorage();
        throw new Error('User session invalid. Logged out successfully.');
      }

      // Don't attempt refresh for token validation endpoint if user was deleted
      if (endpoint === '/auth/me' && response.status === 401) {
        console.log('👤 Token validation failed with 401 - user likely deleted. Clearing tokens without retry.');
        this.clearTokensFromStorage();
        throw new Error('User account no longer exists. Please log in again.');
      }

      // If we get a 401 and have a refresh token, try to refresh
      // Double-check that tokens haven't been cleared during this request
      if (response.status === 401 && this.refreshToken && this.accessToken && !this.isRefreshing) {
        // Check if we've exceeded maximum refresh attempts BEFORE attempting refresh
        if (this.refreshAttempts >= this.maxRefreshAttempts) {
          console.error(`❌ Maximum refresh attempts (${this.maxRefreshAttempts}) reached. Clearing tokens.`);
          this.clearTokensFromStorage();
          // Don't attempt any more requests
          throw new Error('Authentication failed. Please log in again.');
        }

        try {
          this.isRefreshing = true;
          this.refreshAttempts++;
          console.log(`🔄 Attempting token refresh (attempt ${this.refreshAttempts}/${this.maxRefreshAttempts})`);
          
          await this.refreshAccessToken();
          
          // Reset counter on successful refresh
          this.refreshAttempts = 0;
          this.isRefreshing = false;
          
          // Retry the original request with the new token
          return this.makeRequest(endpoint, options);
        } catch (refreshError) {
          this.isRefreshing = false;
          console.error('❌ Token refresh failed:', refreshError);
          
          // If we've reached max attempts, clear tokens and stop
          if (this.refreshAttempts >= this.maxRefreshAttempts) {
            console.error(`❌ Maximum refresh attempts reached. Clearing all tokens.`);
            this.clearTokensFromStorage();
          }
          
          // Always throw error after failed refresh to stop the loop
          throw new Error('Authentication failed. Please log in again.');
        }
      }

      throw new Error(errorData.message);
    }

    try {
      const responseText = await response.text();
      console.log(`📡 [makeRequest] Raw response text length: ${responseText.length}`);
      console.log(`📡 [makeRequest] Raw response text (first 1000 chars):`, responseText.substring(0, 1000));
      
      if (!responseText.trim()) {
        console.error(`📡 [makeRequest] ❌ Empty response body received`);
        throw new Error('Empty response from server');
      }
      
      const result = JSON.parse(responseText);
      console.log('✅ Response data parsed successfully:', result);
      
      // Additional logging for UserGameScore arrays
      if (Array.isArray(result) && result.length > 0 && result[0].scoreId !== undefined) {
        console.log(`📡 [makeRequest] Detected UserGameScore array with ${result.length} items`);
        console.log(`📡 [makeRequest] First item keys:`, Object.keys(result[0]));
      }
      
      return result;
    } catch (parseError) {
      console.error('❌ Error parsing successful response:', parseError);
      
      // If it's a JSON parsing error, provide more context
      if (parseError instanceof SyntaxError) {
        console.error(`📡 [makeRequest] JSON parsing failed. Error:`, parseError.message);
        throw new Error(`Invalid JSON response from server: ${parseError.message}`);
      }
      
      // Re-throw other errors
      throw parseError;
    }
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    this.saveTokensToStorage(response.accessToken, response.refreshToken);
    localStorage.setItem('firedup_user', JSON.stringify(response.user));

    return response;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    this.saveTokensToStorage(response.accessToken, response.refreshToken);
    localStorage.setItem('firedup_user', JSON.stringify(response.user));

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.makeRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Even if the server logout fails, we still want to clear local storage
      console.warn('Server logout failed:', error);
      
      // If it's an authentication error, the user was likely deleted
      if (error instanceof Error && error.message.includes('User session invalid')) {
        console.log('🗑️ User was deleted from database, tokens already cleared');
      }
    } finally {
      // Always ensure tokens are cleared, even if already cleared in makeRequest
      this.clearTokensFromStorage();
    }
  }

  /**
   * Reset refresh attempt counter (useful for debugging or manual intervention)
   */
  resetRefreshAttempts(): void {
    this.refreshAttempts = 0;
    console.log('🔄 Refresh attempt counter reset');
  }

  /**
   * Get current refresh attempt count (useful for debugging)
   */
  getRefreshAttempts(): number {
    return this.refreshAttempts;
  }

  async refreshAccessToken(): Promise<RefreshTokenResponse> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Make a direct fetch call instead of using makeRequest to avoid infinite loop
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Refresh failed' }));
        
        // If user was deleted or token is invalid, clear all tokens
        if (response.status === 401 || response.status === 403) {
          console.log('🔄 User appears to be deleted or token invalid, clearing tokens');
          this.clearTokensFromStorage();
          // Set attempts to max to prevent further retries
          this.refreshAttempts = this.maxRefreshAttempts;
        }
        
        throw new Error(errorData.message || 'Failed to refresh token');
      }

      const data = await response.json();
      
      if (!data.accessToken) {
        throw new Error('Invalid refresh response');
      }

      this.saveTokensToStorage(data.accessToken, data.refreshToken);
      return data;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    return this.makeRequest<User>('/auth/me');
  }

  async validateToken(): Promise<ValidateTokenResponse> {
    return this.makeRequest<ValidateTokenResponse>('/auth/validate');
  }

  /**
   * Validate token without triggering refresh attempts (for initial auth check)
   */
  async validateTokenNoRefresh(): Promise<ValidateTokenResponse> {
    const url = `${this.baseUrl}/auth/validate`;
    console.log('🌐 Making validation request (no refresh) to:', url);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
      console.log('🔐 Authorization header added');
    } else {
      // If no access token, immediately return invalid
      return { valid: false };
    }

    try {
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        console.log('🔒 Token validation failed (no refresh attempted)');
        return { valid: false };
      }

      const result = await response.json();
      console.log('✅ Token validation successful (no refresh)');
      return result;
    } catch (error) {
      console.log('🔒 Token validation error (no refresh attempted):', error);
      return { valid: false };
    }
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.makeRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.makeRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async resetPassword(token: string, email: string, newPassword: string): Promise<{ message: string }> {
    return this.makeRequest(`/auth/reset-password?email=${encodeURIComponent(email)}`, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async verifyEmail(token: string, email: string): Promise<{ message: string }> {
    const response = await this.makeRequest<{ success: boolean; message: string }>(`/verify-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`, {
      method: 'GET',
    });
    
    // The server returns { success: boolean, message: string }
    // but we want to return just { message: string } for consistency
    return { message: response.message };
  }

  async resendEmailVerification(email: string): Promise<{ message: string }> {
    return this.makeRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Get recent high scores
  async getRecentScores(limit: number = 10): Promise<RecentScore[]> {
    return this.makeRequest(`/recent-scores?limit=${limit}`, {
      method: 'GET',
    });
  }

  // Utility method to check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // Get user from local storage (for quick access without API call)
  getCachedUser(): User | null {
    const userData = localStorage.getItem('firedup_user');
    return userData ? JSON.parse(userData) : null;
  }

  // Profile management methods
  async getUserProfile(): Promise<UserProfileData> {
    return this.makeRequest<UserProfileData>('/user/profile', {
      method: 'GET',
    });
  }

  async updateUserProfile(profileData: UpdateProfileRequest): Promise<User> {
    console.log('🔄 Updating user profile with data:', profileData);
    console.log('🔄 API Base URL:', this.baseUrl);
    console.log('🔄 Access Token:', this.accessToken ? 'Present' : 'Missing');
    
    const updatedUser = await this.makeRequest<User>('/user/profile', {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });

    console.log('✅ Profile update successful:', updatedUser);
    // Update cached user data
    localStorage.setItem('firedup_user', JSON.stringify(updatedUser));
    return updatedUser;
  }

  // Get user's top scores by game
  async getUserTopScores(): Promise<PlayerTopScore[]> {
    return this.makeRequest<PlayerTopScore[]>('/user/top-scores', {
      method: 'GET',
    });
  }

  // Get all user's scores for a specific game
  async getUserGameScores(gameId: number): Promise<UserGameScore[]> {
    console.log(`🎮 [getUserGameScores] Requesting scores for game ID: ${gameId}`);
    
    try {
      const result = await this.makeRequest<UserGameScore[]>(`/user/game-scores/${gameId}`, {
        method: 'GET',
      });
      
      console.log(`🎮 [getUserGameScores] ✅ Received ${result.length} scores`);
      console.log(`🎮 [getUserGameScores] Full response:`, result);
      
      // Log the first score to see its structure
      if (result.length > 0) {
        console.log(`🎮 [getUserGameScores] Sample score structure:`, JSON.stringify(result[0], null, 2));
        console.log(`🎮 [getUserGameScores] matchId value:`, result[0].matchId);
        console.log(`🎮 [getUserGameScores] matchId type:`, typeof result[0].matchId);
      }
      
      return result;
    } catch (error) {
      console.error(`🎮 [getUserGameScores] ❌ Error fetching scores for game ${gameId}:`, error);
      throw error;
    }
  }

  // Get detailed match information
  async getMatchDetails(matchId: string): Promise<MatchDetails> {
    console.log(`🎯 [getMatchDetails] Requesting details for match ID: ${matchId}`);
    
    try {
      const result = await this.makeRequest<MatchDetails>(`/user/matches/${matchId}`, {
        method: 'GET'
      });
      
      console.log(`🎯 [getMatchDetails] ✅ Received match details`);
      console.log(`🎯 [getMatchDetails] Match info:`, result);
      
      return result;
    } catch (error) {
      console.error(`🎯 [getMatchDetails] ❌ Error fetching match details for ${matchId}:`, error);
      throw error;
    }
  }

  // Get dashboard data
  async getDashboardData(): Promise<DashboardData> {
    return this.makeRequest<DashboardData>('/dashboard', {
      method: 'GET',
    });
  }

  async getAdminUsers(params: {
    search?: string;
    status?: string;
    role?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<AdminUsersPage> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.set(key, String(value));
    });
    return this.makeRequest<AdminUsersPage>(`/admin/users?${query.toString()}`, {
      method: 'GET'
    });
  }

  async setAdminRole(userId: number, enabled: boolean): Promise<AdminUserSummary> {
    return this.makeRequest<AdminUserSummary>(`/admin/users/${userId}/roles/admin`, {
      method: 'PUT',
      body: JSON.stringify({ enabled })
    });
  }

  async setAdminUserStatus(
    userId: number,
    status: 'active' | 'suspended',
    reason?: string
  ): Promise<AdminUserSummary> {
    return this.makeRequest<AdminUserSummary>(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, reason })
    });
  }

  async getAdminMetrics(startDate: string, endDate: string): Promise<AdminProductMetrics> {
    const query = new URLSearchParams({ startDate, endDate });
    return this.makeRequest<AdminProductMetrics>(`/admin/metrics?${query.toString()}`, {
      method: 'GET'
    });
  }

  async getAdminAuditLog(page = 1, pageSize = 25): Promise<AdminAuditPage> {
    const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    return this.makeRequest<AdminAuditPage>(`/admin/audit?${query.toString()}`, {
      method: 'GET'
    });
  }

  // Match claim methods
  async getMatchClaimData(matchId: string): Promise<MatchClaimData> {
    return this.makeRequest<MatchClaimData>(`/claim/${matchId}`, {
      method: 'GET',
    });
  }

  async claimMatch(matchId: string, claimRequest: ClaimMatchRequest): Promise<MatchClaimData> {
    return this.makeRequest<MatchClaimData>(`/claim/${matchId}/claim`, {
      method: 'POST',
      body: JSON.stringify(claimRequest),
    });
  }

  // Location management methods
  async getUserLocations(): Promise<UserLocation[]> {
    console.log('🏢 [getUserLocations] Requesting user locations');
    
    try {
      const result = await this.makeRequest<UserLocation[]>('/locations', {
        method: 'GET',
      });
      
      console.log(`🏢 [getUserLocations] ✅ Received ${result.length} locations`);
      console.log(`🏢 [getUserLocations] Locations:`, result);
      
      return result;
    } catch (error) {
      console.error(`🏢 [getUserLocations] ❌ Error fetching locations:`, error);
      throw error;
    }
  }

  async registerLocation(locationData: LocationRegistrationRequest): Promise<LocationRegistrationResponse> {
    console.log('🏢 [registerLocation] Registering new location:', locationData);
    
    try {
      const result = await this.makeRequest<LocationRegistrationResponse>('/locations', {
        method: 'POST',
        body: JSON.stringify(locationData),
      });
      
      console.log(`🏢 [registerLocation] ✅ Location registered successfully`);
      console.log(`🏢 [registerLocation] Response:`, result);
      
      return result;
    } catch (error) {
      console.error(`🏢 [registerLocation] ❌ Error registering location:`, error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export types for use in other components
export type {
  User,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  RecentScore,
  ApiError,
  ValidateTokenResponse,
  UserProfileData,
  UpdateProfileRequest,
  PlayerTopScore,
  UserGameScore,
  DashboardData,
  MatchClaimData,
  MatchDetails,
  UserLocation,
  LocationRegistrationRequest,
  LocationRegistrationResponse,
  AdminUserSummary,
  AdminUsersPage,
  AdminProductMetrics,
  AdminAuditEntry,
  AdminAuditPage,
  MetricPoint,
};
